/**
 * Dashboard API client.
 *
 * The server derives customer tenant/branch scope from the signed Clerk session.
 * Only separately server-granted ModelProp staff may request a tenant selector;
 * customer sessions cannot widen their own data access.
 */
import type {
  AccessDenial,
  CallDetail,
  CallFilter,
  CallsResult,
  DashboardContext,
  QueueResult,
  RangeKey,
  SentimentResult,
  SummaryResult,
  TeamResult,
  ThemesResult,
  VoiceStudio,
} from './types';

const BASE = '/v1/dashboard';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /**
     * True when the server could not confirm whether the action took effect.
     *
     * This is NOT a failure and must never be presented as one: retrying is
     * how a possibly-delivered request becomes two. The UI has to say "we are
     * confirming this" and point at Support, not offer a try-again button.
     */
    readonly notified?: 'unknown' | boolean,
    /**
     * The server's explanation of an access refusal.
     *
     * A 403 here is a DECISION, not a fault, and it carries the reason plus the
     * address it applies to. Without this the UI could only render a generic
     * failure with a retry button that would never succeed.
     */
    readonly access?: AccessDenial,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The server told us it does not know whether this landed. */
  get deliveryUnknown(): boolean {
    return this.notified === 'unknown';
  }

  /** A permission decision. Retrying cannot change it. */
  get isAccessDenied(): boolean {
    return this.status === 403;
  }
}

async function get<T>(path: string, params: Record<string, string | number | null | undefined> = {}): Promise<T> {
  const url = new URL(path.startsWith('/v1/') ? path : `${BASE}${path}`, window.location.origin);
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') url.searchParams.set(k, String(v));
  }

  const res = await fetch(url.toString(), {
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let access: AccessDenial | undefined;
    try {
      const body = (await res.json()) as { error?: string; access?: AccessDenial };
      if (body.error) message = body.error;
      access = body.access;
    } catch {
      // Non-JSON error body — keep the status message.
    }
    throw new ApiError(message, res.status, undefined, access);
  }

  return (await res.json()) as T;
}

async function del<T>(path: string): Promise<T> {
  const url = new URL(path, window.location.origin);
  const res = await fetch(url.toString(), {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(data.error ?? `Request failed (${res.status})`, res.status);
  }
  return (await res.json()) as T;
}

async function post<T>(path: string, body?: unknown, params: Record<string, string | number | null | undefined> = {}): Promise<T> {
  const url = new URL(path, window.location.origin);
  for (const [k, v] of Object.entries(params)) if (v != null && v !== '') url.searchParams.set(k, String(v));
  const res = await fetch(url.toString(), { credentials: 'same-origin', headers: { accept: 'application/json', 'content-type': 'application/json' }, method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
  if (!res.ok) {
    // Carry `notified` through: a delivery-unknown response is not a plain
    // failure and the UI has to be able to tell the two apart.
    const data = await res.json().catch(() => ({})) as { error?: string; notified?: 'unknown' | boolean };
    throw new ApiError(data.error ?? `Request failed (${res.status})`, res.status, data.notified);
  }
  return await res.json() as T;
}

export const api = {
  context: (branchId?: string, tenantId?: string) => get<DashboardContext>('/context', { branchId, tenantId }),

  summary: (branchId?: string, range: RangeKey = 'today') =>
    get<SummaryResult>('/summary', { branchId, range }),

  queue: (branchId?: string) => get<QueueResult>('/queue', { branchId }),
  requestCrmActivation: (branchId: string, provider: string, notes?: string) =>
    post<{ id: string; status: 'pending' }>('/v1/dashboard/crm-activation-requests', { provider, notes }, { branchId }),

  calls: (branchId?: string, filter: CallFilter = 'All', cursor?: string | null, range?: RangeKey, date?: string | null) =>
    get<CallsResult>('/calls', { branchId, filter, cursor, limit: 50, range, date }),

  call: (id: string, branchId?: string) => get<CallDetail>(`/calls/${id}`, { branchId }),
  acknowledgeCall: (id: string, branchId?: string) => post<{ ok: boolean }>(`/v1/dashboard/calls/${id}/acknowledge`, undefined, { branchId }),

  themes: (branchId?: string, topic?: string | null) => get<ThemesResult>('/themes', { branchId, topic }),

  sentiment: (branchId?: string, days = 7) => get<SentimentResult>('/sentiment', { branchId, days }),
  askVoiceProp: (question: string, branchId?: string) => post<{ answer: string; model: string }>('/v1/dashboard/ask', { question }, { branchId }),

  team: () => get<TeamResult>('/v1/dashboard/team'),
  invite: (email: string, role: string) =>
    post<{ id: string; email: string; role: string }>('/v1/dashboard/team/invitations', { email, role }),
  revokeInvitation: (id: string) => del<{ ok: boolean }>(`/v1/dashboard/team/invitations/${id}`),
  removeUser: (id: string) => del<{ ok: boolean }>(`/v1/dashboard/team/users/${id}`),

  studio: (branchId: string) => get<VoiceStudio>('/v1/voice-studio/', { branchId }),
  studioVoices: (branchId: string) => get<{ voices: { voice_id: string; voice_name: string | null; provider: string | null; gender: string | null; accent: string | null; preview_audio_url: string | null }[] }>('/v1/voice-studio/voices', { branchId }),
  proposeVoice: (body: Record<string, unknown>) => post<{ id: string; status: string }>('/v1/voice-studio/changes', body),
  lockStudio: (branchId: string) => post<{ id: string; changes: number; digestPrepared: boolean }>('/v1/voice-studio/lock', undefined, { branchId }),
  approveStudio: (branchId: string, id: string) => post<{ id: string; status: string }>(`/v1/voice-studio/approve/${id}`, undefined, { branchId }),
};
